import { CheckCircle2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatPence, SERVICES, useCheckBalance, useServicePrices } from '@/features/payments';
import {
  BalanceDisplay,
  InsufficientBalance,
} from '@/features/payments/components/insufficient-balance';

import { PRICE_LOADING_PLACEHOLDER } from '../lib/tier-pricing';

interface EddUpsellModalProps {
  open: boolean;
  onPurchase: () => void;
  onSkip: () => void;
  onAddFunds: () => void;
  isPurchasing: boolean;
}

/**
 * EDD Upsell Modal - shown after Match Report purchase to offer EDD upgrade.
 * Allows users to add Enhanced Due Diligence (+£15) or skip to view their report.
 */
export function EddUpsellModal({
  open,
  onPurchase,
  onSkip,
  onAddFunds,
  isPurchasing,
}: EddUpsellModalProps) {
  const { data: balanceCheck, isLoading: isCheckingBalance } = useCheckBalance(
    SERVICES.AI_CONFIDENCE_REPORT,
    open,
  );
  const { data: servicePrices } = useServicePrices();
  const matchReportPrice = servicePrices?.find((p) => p.key === SERVICES.MATCH_RISK_REPORT);

  const hasSufficientBalance = balanceCheck?.sufficient ?? false;
  const currentBalance = balanceCheck?.currentBalancePence ?? 0;
  const eddPriceLabel = balanceCheck
    ? formatPence(balanceCheck.requiredAmountPence)
    : PRICE_LOADING_PLACEHOLDER;
  const matchReportLabel = matchReportPrice
    ? matchReportPrice.priceFormatted
    : PRICE_LOADING_PLACEHOLDER;

  return (
    <Dialog open={open} onOpenChange={() => undefined}>
      <DialogContent className="sm:max-w-[600px]" hideCloseButton>
        <DialogHeader className="pt-4 text-center sm:text-center">
          <DialogTitle className="text-[20px] font-semibold text-[#051923]">
            Upgrade to Enhanced Due Diligence
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {/* What they already purchased */}
          <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3">
            <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-600" />
            <span className="text-[14px] text-green-800">
              Match Report ({matchReportLabel}) purchased
            </span>
          </div>

          {/* Descriptive text */}
          <div className="space-y-3 text-[14px] leading-relaxed text-gray-600">
            <p>
              Upgrade your report with Enhanced Due Diligence for a detailed AML analysis. This
              add-on includes:
            </p>
            <ul className="ml-4 list-disc space-y-1">
              <li>Full article content extraction from adverse media sources</li>
              <li>AI-powered relevance analysis and confidence scoring</li>
              <li>Detailed risk assessment with supporting evidence</li>
            </ul>
          </div>

          {/* EDD offer */}
          <div className="rounded-lg border border-[#E5E7EB] p-4">
            <div className="flex items-start gap-3">
              <Search className="h-6 w-6 flex-shrink-0 text-[#131313]" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="text-[16px] font-medium text-[#051923]">
                    Enhanced Due Diligence
                  </span>
                  <span className="text-[18px] font-semibold text-[#131313]">+{eddPriceLabel}</span>
                </div>
                <p className="mt-1 text-[13px] text-gray-500">Add-on to your Match Report</p>
              </div>
            </div>
          </div>

          {/* Balance check */}
          {balanceCheck &&
            !isCheckingBalance &&
            (hasSufficientBalance ? (
              <BalanceDisplay currentBalancePence={currentBalance} />
            ) : (
              <InsufficientBalance
                currentBalancePence={currentBalance}
                requiredAmountPence={balanceCheck.requiredAmountPence}
                shortfall={balanceCheck.shortfall}
                onAddFunds={onAddFunds}
              />
            ))}
        </div>

        <div className="mt-6 flex flex-col gap-3">
          <Button
            onClick={onPurchase}
            disabled={!balanceCheck || !hasSufficientBalance || isPurchasing || isCheckingBalance}
            className="h-[45px] w-full rounded-[5px] bg-[#131313] text-[18px] font-medium leading-none tracking-normal text-white hover:bg-[#131313]/90 disabled:opacity-50"
          >
            {isPurchasing ? 'Processing...' : `Add Enhanced Check (+${eddPriceLabel})`}
          </Button>
          <button
            type="button"
            onClick={onSkip}
            disabled={isPurchasing}
            className="text-[14px] font-medium text-gray-500 underline hover:text-gray-700"
          >
            No thanks, view my report
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
